import { API } from '@/api'
import { LoginScope } from '@/consts/team'
import { getRegisterChannelFromCookies } from '@/utils/adAttribution'
import { RequestConf } from '@/utils/http'
import { getGithubOauthRedirectUri } from '@/utils/oauthRedirect'

export type LoginStep1Result =
  | {
      mfaRequired: false
      token: string
      email: string
      scope?: number
    }
  | {
      mfaRequired: true
      mfaChallenge: string
      email: string
      scope?: number
    }

/**
 * Authentication Service
 */
export class AuthService {
  private static parseLoginResponse(
    res: {
      code: number
      msg: string
      token?: string
      mfaRequired?: boolean
      mfaChallenge?: string
    },
    email: string,
    scope?: number,
  ): LoginStep1Result {
    if (res.code !== 0) {
      throw new Error(res.msg || 'Login failed')
    }

    if (res.mfaRequired) {
      return {
        mfaRequired: true,
        mfaChallenge: res.mfaChallenge || '',
        email,
        scope,
      }
    }

    return {
      mfaRequired: false,
      token: `Bearer ${res.token}`,
      email,
      scope,
    }
  }

  /**
   * Platform login step 1 - credentials verification
   */
  static async platformLoginStep1(email: string, password: string) {
    const res = await API.login({ name: email, password })
    return AuthService.parseLoginResponse(res, email)
  }

  /**
   * Team login step 1 - credentials verification with team scope
   */
  static async teamLoginStep1(email: string, password: string) {
    const res = await API.login({ name: email, password, scope: LoginScope.Team })
    return AuthService.parseLoginResponse(res, email, LoginScope.Team)
  }

  /**
   * Login step 2 - MFA verification
   */
  static async verifyMfaLogin(
    mfaChallenge: string,
    totpCode: string,
    config?: RequestConf,
  ): Promise<{ token: string } | { code: number; msg: string }> {
    const res = await API.login(
      { mfa_challenge: mfaChallenge, totp_code: totpCode },
      { noErrMsg: true, errMsg: false, ...config },
    )

    if (res.code === 0) {
      return { token: `Bearer ${res.token}` }
    }

    return { code: res.code, msg: res.msg }
  }

  static async verifyMfaLoginWithRecovery(
    mfaChallenge: string,
    recoveryCode: string,
    config?: RequestConf,
  ): Promise<{ token: string } | { code: number; msg: string }> {
    const res = await API.login(
      { mfa_challenge: mfaChallenge, totp_recovery_code: recoveryCode },
      { noErrMsg: true, errMsg: false, ...config },
    )

    if (res.code === 0) {
      return { token: `Bearer ${res.token}` }
    }

    return { code: res.code, msg: res.msg }
  }

  /**
   * Platform login - handles platform scope user login
   * @deprecated Use platformLoginStep1 for MFA-aware flows
   */
  static async platformLogin(email: string, password: string) {
    const result = await AuthService.platformLoginStep1(email, password)
    if (result.mfaRequired) {
      throw new Error('MFA required')
    }
    return {
      token: result.token,
      email,
    }
  }

  /**
   * Team login - handles team scope login
   * @deprecated Use teamLoginStep1 for MFA-aware flows
   */
  static async teamLogin(email: string, password: string) {
    const result = await AuthService.teamLoginStep1(email, password)
    if (result.mfaRequired) {
      throw new Error('MFA required')
    }
    return {
      token: result.token,
      email,
      scope: LoginScope.Team,
    }
  }

  /**
   * OAuth login - handles OAuth authentication (Google, etc.)
   */
  static async oauthLogin(oauthCode: string, oauthType: string, inviterCode?: string) {
    const registerChannel = getRegisterChannelFromCookies()
    const requestData: any = {
      oauth_code: oauthCode,
      oauth_type: oauthType,
      inviter_code: inviterCode,
    }

    if (oauthType === 'github') {
      requestData.oauth_redirect_uri = getGithubOauthRedirectUri()
    }

    if (registerChannel) {
      requestData.register_channel = registerChannel
    }

    const res = await API.loginByOauth(requestData)

    if (res.code === 0) {
      return {
        token: `Bearer ${res.token}`,
      }
    } else {
      throw new Error(res.msg || 'OAuth login failed')
    }
  }

  /**
   * OAuth registration - handles OAuth account registration
   */
  static async oauthRegister(oauthCode: string, oauthType: string, password: string) {
    const res = await API.registerByOauth({
      oauth_code: oauthCode,
      oauth_type: oauthType,
      password,
    })

    if (res.code === 0) {
      return {
        token: `Bearer ${res.token}`,
      }
    } else {
      throw new Error(res.msg || 'OAuth registration failed')
    }
  }
}
